import { useMemo } from "react";
import Tooth from "./Tooth";
import { archFdiList } from "./dentition";

export const FULL_ARCH_TOOTH_COUNT = 16;
export const FULL_ARCH_TOOTH_SIZE_MIN_PX = 40;
export const FULL_ARCH_TOOTH_SIZE_MAX_PX = 64;
export const FULL_ARCH_TOOTH_SIZE_RESERVED_PX = 150;
export const SIDE_ARCH_TOOTH_SIZE_MIN_PX = 28;
export const SIDE_ARCH_TOOTH_SIZE_MAX_PX = 72;
export const SIDE_ARCH_TOOTH_SIZE_RESERVED_PX = 72;

export function getFullArchRawToothSizeForWidth(chartCanvasWidth) {
  if (!Number.isFinite(chartCanvasWidth)) return null;
  return (chartCanvasWidth - FULL_ARCH_TOOTH_SIZE_RESERVED_PX) / FULL_ARCH_TOOTH_COUNT;
}

export function getFullArchMinimumCanvasWidth() {
  return (FULL_ARCH_TOOTH_SIZE_MIN_PX * FULL_ARCH_TOOTH_COUNT) + FULL_ARCH_TOOTH_SIZE_RESERVED_PX;
}

// The point where every tooth has already reached its 64px ceiling. Past this
// canvas width the arch stops growing and the surplus becomes dead margin on
// either side of the centred rows, so the layout hands that surplus to the
// procedure rail instead of letting the chart column hoard it.
export function getFullArchMaximumCanvasWidth() {
  return (FULL_ARCH_TOOTH_SIZE_MAX_PX * FULL_ARCH_TOOTH_COUNT) + FULL_ARCH_TOOTH_SIZE_RESERVED_PX;
}

export function canRenderFullArchAtWidth(chartCanvasWidth) {
  const rawToothSize = getFullArchRawToothSizeForWidth(chartCanvasWidth);
  return rawToothSize != null && rawToothSize >= FULL_ARCH_TOOTH_SIZE_MIN_PX;
}

export function getFullArchToothSizeClamp() {
  return `clamp(${FULL_ARCH_TOOTH_SIZE_MIN_PX}px, calc((100cqw - ${FULL_ARCH_TOOTH_SIZE_RESERVED_PX}px) / ${FULL_ARCH_TOOTH_COUNT}), ${FULL_ARCH_TOOTH_SIZE_MAX_PX}px)`;
}

export function getSideArchToothSizeClamp() {
  return `clamp(${SIDE_ARCH_TOOTH_SIZE_MIN_PX}px, calc((100cqw - ${SIDE_ARCH_TOOTH_SIZE_RESERVED_PX}px) / 8), ${SIDE_ARCH_TOOTH_SIZE_MAX_PX}px)`;
}

// =====================================================================
// ToothChart — orchestrates upper + lower arches.
// =====================================================================
//
// Dentition modes:
//   'permanent' → adult teeth only (2 rows: upper, lower).
//   'primary'   → baby teeth only (2 rows: upper, lower).
//   'both'      → permanent stacked above primary (4 rows total).
//
// View modes:
//   'full'  → both halves of each arch in one row, with a midline
//             divider down the centre. Default on desktop.
//   'right' → patient's right side only (Q1+Q4 / Q5+Q8).
//   'left'  → patient's left side only (Q2+Q3 / Q6+Q7).
// =====================================================================
export default function ToothChart({
  state,
  traceState = null,
  findings = [],
  mode = "permanent",               // 'permanent' | 'both' | 'primary'
  viewMode = "full",                // 'full' | 'right' | 'left'
  active = null,
  readOnly = false,
  // Notes can be editable even when the chart's PAINT layer is readOnly
  // (e.g. on Tx Plan we want users to add a note before they've armed a
  // procedure). Defaults to !readOnly for backward compat with charting.
  canEditNote,
  // True on the Tx Plan chart so the per-tooth hover card shows
  // "On the plan / From the chart" labels instead of "Findings".
  isTxMode = false,
  planDotsByTooth = null,           // { "16": ["#3b82f6", ...], ... }
  completedWorkByTooth = null,      // { "16": [{ completed work row }, ...], ... }
  dimFindings = false,              // dim the findings layer (chairside non-chart modes)
  // Overlay paint layer — same chart-state shape, but painted ON TOP
  // of the base findings (so e.g. a painted Composite covers any
  // caries on the same surface). Used by Doctor view in Today / Tx
  // Plan / Future Need so painted procedures render as colored
  // surfaces over the dimmed existing chart.
  overlayState = null,
  overlayFindings = null,           // [{ code, color_hex }, ...]
  stackSummaryByTooth = null,       // { "14": { stack_count, stacked_procedures, ... } }
  // Multi-tooth groupings — each group paints a colored frame +
  // procedure label behind the contiguous teeth in the same arch row
  // so a dentist can tell a Bridge from a Denture from an RPD at a
  // glance. Shape: [{ id, label: "BRIDGE", color: "#b91c1c", teeth:
  // ["16","15","14","13"] }]. Adjacency / arch grouping is enforced
  // upstream (BuildMultiToothDialog); we just render whatever's here.
  multiToothGroups = null,
  onApplyFindingSurface,
  onEraseSurface,
  onClearTooth,
  onOpenNote,
  size = 60,
}) {
  const noteEditable = canEditNote == null ? !readOnly : !!canEditNote;
  // Per-tooth hover card summarises findings by name. Build the
  // code→name map once from the same `findings` catalog that drives
  // colorByCode — this includes the synthetic Tx Plan procedure
  // entries (TreatmentTab passes them in via combinedFindings).
  const nameByCode = useMemo(
    () => Object.fromEntries(findings.map((f) => [f.code, f.name])),
    [findings],
  );
  const colorByCode = useMemo(
    () => Object.fromEntries(findings.map((f) => [f.code, f.color_hex])),
    [findings],
  );
  const pulpByCode = useMemo(
    () => Object.fromEntries(findings.map((f) => [f.code, !!f.is_pulp_involvement])),
    [findings],
  );
  const overlayColorByCode = useMemo(
    () => overlayFindings
      ? Object.fromEntries(overlayFindings.map((f) => [f.code, f.color_hex]))
      : null,
    [overlayFindings],
  );
  const overlayPulpByCode = useMemo(
    () => overlayFindings
      ? Object.fromEntries(overlayFindings.map((f) => [f.code, !!f.is_pulp_involvement]))
      : null,
    [overlayFindings],
  );

  // Map FDI → group it belongs to (for fast per-tooth lookup when
  // walking a row). Adjacency means a tooth can only be in one group at
  // a time; if we ever allow overlap we'll need a list here.
  // Each entry attaches the per-tooth role from the group's rolesByFdi
  // so the cell can render an A (abutment) / P (pontic) chip.
  const groupByFdi = useMemo(() => {
    if (!Array.isArray(multiToothGroups) || multiToothGroups.length === 0) return null;
    const out = {};
    for (const g of multiToothGroups) {
      if (!g || !Array.isArray(g.teeth)) continue;
      for (const t of g.teeth) {
        const key = String(t);
        if (!out[key]) {
          out[key] = { ...g, role: g.rolesByFdi?.[key] ?? null };
        }
      }
    }
    return out;
  }, [multiToothGroups]);

  const isFull = viewMode === "full";
  const archSide = isFull ? "both" : viewMode;

  function entryFor(fdi) {
    if (!state?.teeth) return null;
    return state.teeth[String(fdi)] ?? null;
  }
  function traceEntryFor(fdi) {
    if (!traceState?.teeth) return null;
    return traceState.teeth[String(fdi)] ?? null;
  }
  function overlayEntryFor(fdi) {
    if (!overlayState?.teeth) return null;
    return overlayState.teeth[String(fdi)] ?? null;
  }

  // Build props for one tooth. Returning a props object (instead of
  // wrapping <Tooth/> inside an inline `function ToothSlot()` component
  // declared in the parent body) keeps Tooth's React element type
  // stable across re-renders. Otherwise React treats each render's
  // wrapper as a brand-new component type and unmounts every Tooth,
  // wiping per-tooth interaction refs.
  function toothProps(fdi, arch) {
    return {
      key: `${arch}-${fdi}`,
      fdi,
      entry: entryFor(fdi),
      traceEntry: traceEntryFor(fdi),
      colorByCode,
      pulpByCode,
      active,
      readOnly,
      canEditNote: noteEditable,
      nameByCode,
      isTxMode,
      planDots: planDotsByTooth?.[String(fdi)] ?? null,
      completedWork: completedWorkByTooth?.[String(fdi)] ?? [],
      dimFindings,
      overlayEntry: overlayEntryFor(fdi),
      overlayColorByCode,
      overlayPulpByCode,
      stackSummary: stackSummaryByTooth?.[String(fdi)] ?? null,
      onApplyFindingSurface,
      onEraseSurface,
      onClearTooth,
      onOpenNote,
      size,
    };
  }

  // Compute, for each FDI in a row, whether it sits at the start /
  // middle / end of its contiguous group run within THAT row. A row
  // boundary or a midline split breaks a run, so a Bridge that spans
  // #14-#11 + #21-#23 (across midline) renders as two visually
  // separate segments — each correctly framed.
  function segmentEdges(fdis, idx) {
    const fdi = String(fdis[idx]);
    const group = groupByFdi?.[fdi];
    if (!group) return null;
    const prev = idx > 0 ? String(fdis[idx - 1]) : null;
    const next = idx < fdis.length - 1 ? String(fdis[idx + 1]) : null;
    const prevSame = prev && groupByFdi?.[prev]?.id === group.id;
    const nextSame = next && groupByFdi?.[next]?.id === group.id;
    return { isStart: !prevSame, isEnd: !nextSame };
  }

  function MidlineDivider() {
    return (
      <div
        aria-hidden
        className="w-px self-stretch bg-slate-300 mx-2"
        data-testid="midline-divider"
      />
    );
  }

  // Render one half-row of teeth, tracking each FDI's start/end edges
  // within its multi-tooth group so the frame draws correctly. When
  // a tooth has no group we still emit the same wrapper shape (just
  // without the frame element) so React's reconciler doesn't unmount
  // the <Tooth/> when its group toggles on or off — keeping useRef
  // gesture state inside Tooth stable across renders. The wrapper itself uses native HTML
  // elements only, so it has no per-render component-type churn.
  function renderHalf(arch, halfFdis) {
    return halfFdis.map((fdi, idx) => {
      const tprops = toothProps(fdi, arch);
      const { key: toothKey, ...toothRenderProps } = tprops;
      const edges  = segmentEdges(halfFdis, idx);
      const group  = edges ? groupByFdi[String(fdi)] : null;
      const color  = group?.color || "#475569";
      const activeDenture = group?.installed === false;
      const tint   = group ? `${color}${activeDenture ? "0d" : "1a"}` : "transparent";
      const borderStyle = activeDenture ? "dashed" : "solid";
      const borderW = 2;
      return (
        <div
          key={toothKey}
          className="relative"
          style={{ width: `calc(var(--tooth-size, ${size}px) + 4px)` }}
        >
          {group ? (
            <div
              aria-hidden
              className="absolute pointer-events-none"
              data-testid={`multi-tooth-band-${group.id ?? group.label}-${fdi}`}
              style={{
                top: 0,
                bottom: 0,
                left:  edges.isStart ? 0 : -3,
                right: edges.isEnd   ? 0 : -3,
                backgroundColor: tint,
                borderTop:    `${borderW}px ${borderStyle} ${color}`,
                borderBottom: `${borderW}px ${borderStyle} ${color}`,
                borderLeft:   edges.isStart ? `${borderW}px ${borderStyle} ${color}` : "none",
                borderRight:  edges.isEnd   ? `${borderW}px ${borderStyle} ${color}` : "none",
                borderTopLeftRadius:     edges.isStart ? 8 : 0,
                borderBottomLeftRadius:  edges.isStart ? 8 : 0,
                borderTopRightRadius:    edges.isEnd   ? 8 : 0,
                borderBottomRightRadius: edges.isEnd   ? 8 : 0,
                zIndex: 0,
              }}
            />
          ) : null}
          <div className="relative" style={{ zIndex: 1 }}>
            <Tooth {...toothRenderProps} />
          </div>
          {group && edges.isStart ? (
            <span
              className="absolute pointer-events-none"
              style={{
                top: -8,
                left: 6,
                backgroundColor: "#ffffff",
                color,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.06em",
                lineHeight: "12px",
                padding: "0 5px",
                borderRadius: 3,
                zIndex: 2,
                whiteSpace: "nowrap",
              }}
              data-testid={`multi-tooth-label-${group.id ?? group.label}-${fdi}`}
            >
              {group.label}
            </span>
          ) : null}
          {/* Role chip — A (abutment, anchor crown) or P (pontic, the
              fake tooth in the middle). Only renders when the group has
              roles assigned per tooth (today: bridges; future: any
              role-required multi-tooth procedure). Sits in the
              bottom-right of the cell so it doesn't overlap the FDI
              label up top or the procedure-name callout. */}
          {group && group.role ? (
            <span
              className="absolute pointer-events-none"
              style={{
                bottom: 2,
                right: 4,
                width: 14,
                height: 14,
                borderRadius: 7,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0,
                lineHeight: "14px",
                textAlign: "center",
                backgroundColor: group.role === "abutment" ? "#16a34a" : "#d97706",
                color: "#ffffff",
                zIndex: 3,
                boxShadow: "0 0 0 1.5px #ffffff",
              }}
              title={group.role === "abutment" ? "Abutment (anchor crown)" : "Pontic (replacement tooth)"}
              data-testid={`multi-tooth-role-${group.id ?? group.label}-${fdi}`}
            >
              {group.role === "abutment" ? "A" : "P"}
            </span>
          ) : null}
        </div>
      );
    });
  }

  function renderFullRow(arch, fdis, alignBottom) {
    const half = Math.floor(fdis.length / 2);
    const right = fdis.slice(0, half);
    const left  = fdis.slice(half);
    return (
      <div
        className={`flex w-max min-w-full justify-center gap-0.5 min-[360px]:gap-1 ${alignBottom ? "items-end" : "items-start"}`}
        data-testid="tooth-chart-row-full"
      >
        <div className="flex gap-0.5 min-[360px]:gap-1">
          {renderHalf(arch, right)}
        </div>
        <MidlineDivider />
        <div className="flex gap-1">
          {renderHalf(arch, left)}
        </div>
      </div>
    );
  }

  function renderSideRow(arch, fdis, alignBottom, side) {
    return (
      <div
        className={`flex w-max min-w-full justify-center gap-1 ${alignBottom ? "items-end" : "items-start"}`}
        data-testid="tooth-chart-row-side"
      >
        {side === "left" ? <MidlineDivider /> : null}
        <div className="flex gap-1">
          {renderHalf(arch, fdis)}
        </div>
        {side === "right" ? <MidlineDivider /> : null}
      </div>
    );
  }

  function renderRow(arch, fdis, alignBottom) {
    return isFull
      ? renderFullRow(arch, fdis, alignBottom)
      : renderSideRow(arch, fdis, alignBottom, viewMode);
  }

  function DentitionBlock({ dentition }) {
    const upperFdis = archFdiList("upper", dentition, archSide);
    const lowerFdis = archFdiList("lower", dentition, archSide);
    return (
      <div className="space-y-2">
        {renderRow("upper", upperFdis, true)}
        <div className="border-t border-dashed border-slate-200 my-2" />
        {renderRow("lower", lowerFdis, false)}
      </div>
    );
  }

  // Fluid tooth size — clamps between a usable touch-target minimum and a
  // comfortable maximum, scaling with the chart container, not the whole
  // viewport. Full arch stays readable within a bounded range; ChartingTab
  // switches to side-view before it would need to shrink below that floor.
  const toothSize = isFull
    ? getFullArchToothSizeClamp()
    : getSideArchToothSizeClamp();

  const showPermanent = mode === "permanent" || mode === "both";
  const showPrimary   = mode === "primary"   || mode === "both";
  const testAttrs = process.env.NODE_ENV === "test"
    ? {
        "data-state": JSON.stringify(state ?? {}),
        "data-completed-work-by-tooth": JSON.stringify(completedWorkByTooth ?? {}),
        "data-trace-state": traceState ? "present" : "none",
        "data-view-mode": viewMode,
      }
    : {};

  return (
    <div
      data-testid="tooth-chart"
      {...testAttrs}
      style={{ containerType: "inline-size", "--tooth-size": toothSize }}
    >
      {showPermanent ? <DentitionBlock dentition="permanent" /> : null}
      {mode === "both" ? (
        <div className="my-4 flex items-center gap-3" aria-hidden>
          <div className="flex-1 border-t border-slate-200" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Primary
          </span>
          <div className="flex-1 border-t border-slate-200" />
        </div>
      ) : null}
      {showPrimary ? <DentitionBlock dentition="primary" /> : null}
    </div>
  );
}
